import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import { appConfig } from '@/config/appConfig';
import { configurationCache } from '@/configuration/ConfigurationCache';
import type {
  BrandingSettings,
  GeneralSettings,
  NotificationSettings,
  SecuritySettings,
  SystemSettings,
} from '@/configuration/ConfigurationTypes';
import { normalizeSystemSettings } from '@/configuration/normalizeSystemSettings';

let inMemorySettings: SystemSettings | null = null;

const mockDelayMs = 180;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), mockDelayMs);
  });
}

function buildDefaultSettings(): SystemSettings {
  return {
    general: {
      portalName: appConfig.name,
      organizationName: 'Creditsafe',
      defaultLanguage: 'en-US',
      sessionTimeoutMinutes: 60,
      maintenanceMode: false,
      maintenanceBannerMessage: '',
      portalVersion: '4.12.0',
    },
    notifications: {
      emailAlertsEnabled: true,
      slackIntegrationEnabled: true,
      webhookEndpoint: 'https://hooks.slack.com/services/T00/B00/X00',
      alertSeverityThreshold: 'high',
      notificationRetryCount: 3,
      notificationTimeoutSeconds: 30,
    },
    security: {
      enforceMfa: true,
      passwordPolicy: 'strong',
      corporateIpAllowList: '192.168.1.0/24, 10.0.4.0/16',
      maximumConcurrentSessions: 3,
      sessionExpirationMinutes: 120,
      idleTimeoutMinutes: 30,
    },
    branding: {
      portalLogoUrl: '/favicon.ico',
      companyLogoUrl: '/favicon.ico',
      primaryBrandColor: '#c8102e',
      secondaryBrandColor: '#1f2937',
      systemTheme: 'light',
      footerText: 'Creditsafe Global',
    },
    version: 1,
  };
}

function getCurrentSettings(): SystemSettings {
  const defaults = buildDefaultSettings();

  if (inMemorySettings) {
    return normalizeSystemSettings(inMemorySettings, defaults);
  }

  const cached = configurationCache.read();

  if (cached) {
    inMemorySettings = normalizeSystemSettings(cached, defaults);
    return inMemorySettings;
  }

  inMemorySettings = defaults;
  configurationCache.write(defaults);

  return defaults;
}

function persist(next: SystemSettings): SystemSettings {
  const normalized = normalizeSystemSettings(next, buildDefaultSettings());
  inMemorySettings = normalized;
  configurationCache.write(normalized);
  return normalized;
}

export const configurationService = {
  async getSystemSettings(): Promise<SystemSettings> {
    try {
      const response = await apiClient.get<SystemSettings>(
        endpoints.systemSettings,
      );
      return persist(response.data);
    } catch {
      return delay(getCurrentSettings());
    }
  },

  async updateGeneralSettings(
    payload: GeneralSettings,
  ): Promise<SystemSettings> {
    try {
      await apiClient.put(endpoints.systemSettingsGeneral, payload);
    } catch {
      // Fallback path keeps local state for API-offline mode.
    }

    const current = getCurrentSettings();
    return delay(
      persist({
        ...current,
        general: payload,
        version: current.version + 1,
      }),
    );
  },

  async updateNotificationSettings(
    payload: NotificationSettings,
  ): Promise<SystemSettings> {
    try {
      await apiClient.put(endpoints.systemSettingsNotifications, payload);
    } catch {
      // Fallback path keeps local state for API-offline mode.
    }

    const current = getCurrentSettings();
    return delay(
      persist({
        ...current,
        notifications: payload,
        version: current.version + 1,
      }),
    );
  },

  async updateSecuritySettings(
    payload: SecuritySettings,
  ): Promise<SystemSettings> {
    try {
      await apiClient.put(endpoints.systemSettingsSecurity, payload);
    } catch {
      // Fallback path keeps local state for API-offline mode.
    }

    const current = getCurrentSettings();
    return delay(
      persist({
        ...current,
        security: payload,
        version: current.version + 1,
      }),
    );
  },

  async updateBrandingSettings(
    payload: BrandingSettings,
  ): Promise<SystemSettings> {
    try {
      await apiClient.put(endpoints.systemSettingsBranding, payload);
    } catch {
      // Fallback path keeps local state for API-offline mode.
    }

    const current = getCurrentSettings();
    return delay(
      persist({
        ...current,
        branding: payload,
        version: current.version + 1,
      }),
    );
  },

  async uploadPortalLogo(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post<{ url: string }>(
        endpoints.systemLogo,
        formData,
      );

      return response.data;
    } catch {
      return delay({ url: URL.createObjectURL(file) });
    }
  },

  async uploadFavicon(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post<{ url: string }>(
        endpoints.systemFavicon,
        formData,
      );

      return response.data;
    } catch {
      return delay({ url: URL.createObjectURL(file) });
    }
  },
};
